<script lang="ts">
	import { onMount } from 'svelte';
	import { X } from '@lucide/svelte';
	import { fly, fade } from 'svelte/transition';
	import { Input } from '$lib/components/ui/input';
	import { Button } from '$lib/components/ui/button';
	import { toast } from 'svelte-sonner';
	import type { ExitIntentPopup } from '$lib/server/db/schema';

	const STORAGE_KEY = 'exit-intent-seen';

	let popup = $state<ExitIntentPopup | null>(null);
	let visible = $state(false);
	let email = $state('');
	let isSubmitting = $state(false);

	// --- Chargement config ---
	async function loadConfig() {
		if (sessionStorage.getItem(STORAGE_KEY) === '1') return;
		const res = await fetch('/api/exit-intent');
		if (!res.ok) return;
		popup = await res.json();
	}

	// --- Analytics ---
	function track(type: 'impression' | 'close' | 'conversion') {
		if (!popup) return;
		navigator.sendBeacon('/api/exit-intent/track', JSON.stringify({ popup_id: popup.id, type }));
	}

	// --- Déclencheurs ---
	function show() {
		if (!popup || sessionStorage.getItem(STORAGE_KEY) === '1') return;
		visible = true;
		track('impression');
	}

	function handleMouseLeave(e: MouseEvent) {
		if (e.clientY <= 0) show();
	}

	function handleScroll() {
		const scrolled = window.scrollY + window.innerHeight;
		const total = document.documentElement.scrollHeight;
		const threshold = (popup?.scroll_threshold ?? 70) / 100;
		if (scrolled / total >= threshold) {
			show();
			window.removeEventListener('scroll', handleScroll);
		}
	}

	function dismiss() {
		visible = false;
		sessionStorage.setItem(STORAGE_KEY, '1');
		track('close');
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') dismiss();
	}

	onMount(() => {
		let timer: ReturnType<typeof setTimeout>;

		loadConfig().then(() => {
			if (!popup) return;

			timer = setTimeout(() => {
				document.addEventListener('mouseleave', handleMouseLeave);
				window.addEventListener('scroll', handleScroll, { passive: true });
			}, popup.delay_ms);
		});

		// ✅ Cleanup synchrone — Svelte peut l'utiliser correctement
		return () => {
			clearTimeout(timer);
			document.removeEventListener('mouseleave', handleMouseLeave);
			window.removeEventListener('scroll', handleScroll);
		};
	});

	// --- Soumission ---
	async function handleSubmit(e: Event) {
		e.preventDefault();
		if (!popup) return;
		isSubmitting = true;
		try {
			const response = await fetch('/api/newsletter/subscribe', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email })
			});
			const result = await response.json();
			if (result.success) {
				track('conversion');
				window.location.href = popup.redirect_url;
			} else {
				toast.error('Une erreur est survenue. Réessaie.');
			}
		} catch {
			toast.error('Une erreur est survenue. Réessaie.');
		} finally {
			isSubmitting = false;
		}
	}
</script>

<svelte:window onkeydown={handleKeydown} />

{#if visible && popup}
	<div
		transition:fade={{ duration: 200 }}
		class="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
		onclick={dismiss}
		role="presentation"
	></div>

	<div
		transition:fly={{ y: -30, duration: 300 }}
		class="fixed top-1/2 left-1/2 z-[101] w-full max-w-md
           -translate-x-1/2 -translate-y-1/2 px-4"
		role="dialog"
		aria-modal="true"
		aria-labelledby="exit-popup-title"
	>
		<div class="relative rounded-2xl border border-border bg-card p-8 shadow-2xl">
			<button
				onclick={dismiss}
				class="absolute top-4 right-4 rounded-full p-1.5
               text-muted-foreground transition-colors hover:bg-muted"
				aria-label="Fermer"
			>
				<X class="h-4 w-4" />
			</button>

			<div class="mb-6 text-center">
				<div
					class="mx-auto mb-4 flex h-16 w-16 items-center justify-center
                    rounded-full bg-juslin-gold/10 text-3xl"
				>
					{popup.emoji}
				</div>
				<h2 id="exit-popup-title" class="mb-2 text-xl font-bold text-foreground">
					{popup.title}
				</h2>
				<p class="text-sm text-muted-foreground">
					{@html popup.description}
				</p>
			</div>

			{#if popup.benefits.length > 0}
				<div class="mb-6 space-y-2">
					{#each popup.benefits as benefit}
						<div class="flex items-center gap-2 text-sm text-muted-foreground">
							<span
								class="flex h-4 w-4 shrink-0 items-center justify-center
                           rounded-full bg-juslin-gold text-[10px] font-bold text-black"
							>
								✓
							</span>
							{benefit}
						</div>
					{/each}
				</div>
			{/if}

			<form onsubmit={handleSubmit} class="space-y-3">
				<Input
					type="email"
					bind:value={email}
					placeholder="votre@email.com"
					autocomplete="email"
					required
					class="h-12 text-base"
				/>
				<Button
					type="submit"
					disabled={isSubmitting || !email}
					class="h-12 w-full rounded-full bg-juslin-gold font-bold
                 text-black hover:bg-juslin-gold/90"
				>
					{isSubmitting ? 'Envoi...' : popup.cta_label}
				</Button>
			</form>

			<button
				onclick={dismiss}
				class="mt-4 w-full text-center text-xs text-muted-foreground
               underline transition-colors hover:text-foreground"
			>
				{popup.dismiss_label}
			</button>
		</div>
	</div>
{/if}
